import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Ruler, Plus, Trash2 } from 'lucide-react';
import { getAvailableHeightRange } from '../data/mockPricing';

const DimensionInput = ({ length, height, onLengthChange, onHeightChange, selectedModel, includePosts, panels, onPanelsChange }) => {
  if (!selectedModel) return null;

  const heightRange = getAvailableHeightRange(selectedModel.id);

  // For fence with posts - total length input
  if (includePosts) {
    return (
      <Card className="transition-all duration-300 hover:shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-emerald-600" />
            Abmessungen eingeben
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="length" className="text-base font-medium">
              Gesamtlänge des Zauns (cm)
            </Label>
            <Input
              id="length"
              type="number"
              min="100"
              max="10000"
              value={length}
              onChange={(e) => onLengthChange(e.target.value)}
              placeholder="z.B. 1000"
              className="text-lg h-12 transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="height" className="text-base font-medium">
              Zaunhöhe (cm)
            </Label>
            <Input
              id="height"
              type="number"
              min={heightRange.min}
              max={heightRange.max}
              value={height}
              onChange={(e) => onHeightChange(e.target.value)}
              placeholder="z.B. 120"
              className="text-lg h-12 transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
            />
            <p className="text-sm text-gray-500">
              Verfügbare Höhen: {heightRange.min}-{heightRange.max} cm für {selectedModel.name}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // For panels only - individual panel configuration
  const handleAddPanel = () => {
    onPanelsChange([...panels, { width: '', height: '', quantity: 1 }]);
  };

  const handleRemovePanel = (index) => {
    const newPanels = panels.filter((_, i) => i !== index);
    onPanelsChange(newPanels);
  };

  const handlePanelChange = (index, field, value) => {
    const newPanels = [...panels];
    newPanels[index][field] = value;
    onPanelsChange(newPanels);
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-emerald-600" />
          Zaunelemente konfigurieren
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {panels.map((panel, index) => (
          <div key={index} className="p-4 border-2 border-gray-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Zaun #{index + 1}</span>
              {panels.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemovePanel(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Breite (cm)</Label>
                <Input
                  type="number"
                  min="50"
                  max="200"
                  value={panel.width}
                  onChange={(e) => handlePanelChange(index, 'width', e.target.value)}
                  placeholder="z.B. 200"
                  className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Höhe (cm)</Label>
                <Input
                  type="number"
                  min={heightRange.min}
                  max={heightRange.max}
                  value={panel.height}
                  onChange={(e) => handlePanelChange(index, 'height', e.target.value)}
                  placeholder="z.B. 120"
                  className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Anzahl</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={panel.quantity}
                  onChange={(e) => handlePanelChange(index, 'quantity', e.target.value)}
                  placeholder="1"
                  className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>
        ))}

        <Button
          onClick={handleAddPanel}
          variant="outline"
          className="w-full border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition-all"
        >
          <Plus className="w-4 h-4 mr-2" />
          Weiteres Zaunelement hinzufügen
        </Button>
      </CardContent>
    </Card>
  );
};

export default DimensionInput;